import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import {CREDIT_NOTE_STATUS_ENUM} from "@/models/creditNoteStatusEnum";
import {DEPOSIT_STATUS_ENUM, getDepositStatus} from "@/models/depositStatusEnum";
import {PURCHASE_STATUS_ENUM, documentSign, round2} from "@/purchase/utils";
import {orderShare} from "@/purchase/allocations";
import {isSubjectToVatOnDay} from "@/purchase/vatRegime";
import {documentRef} from "@/accounting/services/export/AccountingFormat";

// Étapes d'un dossier, déduites de l'état de ses contrats (mêmes valeurs que les statuts, pour partager leurs couleurs)
export const ORDER_STAGE = {
    CREATION: {value: "CREATION", label: "Création"},
    ACCEPTED: {value: "ACCEPTED", label: "Acceptée"},
    IN_PROGRESS: {value: "IN_PROGRESS", label: "En cours"},
    DONE: {value: "DONE", label: "Terminée"},
    ABORTED: {value: "ABORTED", label: "Abandonné"},
};
export const ORDER_STAGES = Object.values(ORDER_STAGE);

/**
 * Étape d'un dossier : « abandonné » dès qu'un devis est refusé ou annulé ; sinon un devis en cours de réalisation la rend « en cours »,
 * sinon un devis accepté la rend « acceptée », sinon le dossier est « terminé » dès qu'une facture existe.
 */
export function getOrderStage(order) {
    if ((order.businesses ?? []).some(isDroppedBusiness)) return ORDER_STAGE.ABORTED.value;

    let stage = ORDER_STAGE.CREATION.value;
    for (const business of order.businesses ?? []) {
        const isInvoice = !!business.invoiceNumber;
        if (!isInvoice && business.status === BUSINESS_STATUS_ENUM.IN_PROGRESS.value) {
            stage = ORDER_STAGE.IN_PROGRESS.value;
        }
        if (stage !== ORDER_STAGE.IN_PROGRESS.value && !isInvoice && business.status === BUSINESS_STATUS_ENUM.ACCEPTED.value) {
            stage = ORDER_STAGE.ACCEPTED.value;
        }
        if (stage !== ORDER_STAGE.IN_PROGRESS.value && stage !== ORDER_STAGE.ACCEPTED.value && isInvoice) {
            stage = ORDER_STAGE.DONE.value;
        }
    }
    return stage;
}

export function stageLabel(stage) {
    return ORDER_STAGE[stage]?.label ?? '';
}

// Devis annulé ou refusé : il ne compte plus dans le dossier
export function isDroppedBusiness(business) {
    return !business.invoiceNumber && [BUSINESS_STATUS_ENUM.ABORTED.value, BUSINESS_STATUS_ENUM.REFUSED.value].includes(business.status);
}

/**
 * Documents d'un contrat dans l'ordre de leur vie : devis, facture d'acompte, facture, avoirs.
 * Le statut d'une facture est celui du contrat ; le devis d'un contrat facturé est terminé.
 */
export function contractDocuments(business) {
    const documents = [{
        key: 'quote', type: 'quote', label: 'Devis', ref: documentRef('D', business.number),
        status: business.invoiceNumber ? BUSINESS_STATUS_ENUM.DONE.value : business.status, statusEnum: BUSINESS_STATUS_ENUM,
    }];

    const depositStatus = getDepositStatus(business);
    if (depositStatus) {
        documents.push({
            key: 'deposit', type: 'deposit', label: 'Acompte', ref: documentRef('AC', business.depositNumber),
            status: depositStatus, statusEnum: DEPOSIT_STATUS_ENUM,
        });
    }
    if (business.invoiceNumber) {
        documents.push({
            key: 'invoice', type: 'invoice', label: 'Facture', ref: documentRef('FA', business.invoiceNumber),
            status: business.status, statusEnum: BUSINESS_STATUS_ENUM,
        });
    }
    for (const creditNote of business.creditNotes ?? []) {
        documents.push({
            key: 'creditNote' + creditNote.number, type: 'creditNote', creditNoteNumber: creditNote.number, label: 'Avoir', ref: documentRef('AV', creditNote.number),
            status: creditNote.status, statusEnum: CREDIT_NOTE_STATUS_ENUM,
        });
    }
    return documents;
}

/**
 * Achats d'un dossier avec leur part (la somme des parts de l'achat sur ce dossier : une part par article, par exemple).
 * Un avoir vient en déduction : sa part (share) est négative, comme son reste à recevoir.
 * Les achats « à vérifier » (factures reçues pas encore validées) sont listés mais ne comptent pas dans les totaux :
 * ce ne sont pas encore des dépenses confirmées.
 */
export function purchasesOfOrder(purchases, orderId, vatRegimes = []) {
    return purchases
        .filter(purchase => (purchase.allocations ?? []).some(allocation => String(allocation.orderId) === String(orderId)))
        .map(purchase => {
            const sign = documentSign(purchase);
            const share = orderShare(purchase, orderId);
            const isToReview = purchase.status === PURCHASE_STATUS_ENUM.TO_REVIEW.value;
            const ratio = purchase.htAmount > 0 ? share / purchase.htAmount : 0;
            // Achat daté d'une période sans TVA (franchise en base) : la TVA du fournisseur n'est pas récupérable, le coût est le TTC.
            // Les montants HT / TVA de l'achat restent ceux enregistrés : seul le calcul du coût suit le régime en vigueur à sa date.
            const isTtc = !isSubjectToVatOnDay(vatRegimes, purchase.invoiceDate);
            const cost = isTtc && purchase.htAmount > 0 ? round2(share * (purchase.ttcAmount ?? purchase.htAmount) / purchase.htAmount) : share;
            return {
                purchase, ratio, isToReview, isTtc,
                share: sign * cost,
                // Reste à payer TTC revenant au dossier, au prorata de sa part
                remainingShare: isToReview ? 0 : round2(sign * (purchase.remainingAmount || 0) * ratio),
            };
        })
        .sort((a, b) => new Date(b.purchase.invoiceDate) - new Date(a.purchase.invoiceDate));
}

/**
 * Chiffres d'un dossier : vendu (contrats), acheté (achats ventilés) et marge.
 * `hasPurchase` : sans l'option achats, seul le vendu est connu.
 */
export function orderFinance(order, items, hasPurchase) {
    const sold = order.soldHtAmount ?? 0;
    const pending = order.pendingHtAmount ?? 0;
    if (!hasPurchase) return {sold, pending, hasPurchase: false};

    const confirmed = items.filter(item => !item.isToReview);
    const bought = round2(confirmed.reduce((sum, item) => sum + item.share, 0));
    const toReview = round2(items.filter(item => item.isToReview).reduce((sum, item) => sum + item.share, 0));
    const margin = round2(sold - bought);
    // Base du coût d'achat : HT, TTC (périodes sans TVA) ou un mélange des deux
    const ttcCount = confirmed.filter(item => item.isTtc).length;
    const basis = ttcCount === 0 ? 'HT' : (ttcCount === confirmed.length ? 'TTC' : 'HT/TTC');
    return {
        hasPurchase: true, sold, pending, bought, toReview, margin, basis,
        marginRate: sold > 0 ? Math.round(margin / sold * 1000) / 10 : undefined,
        remaining: round2(confirmed.reduce((sum, item) => sum + item.remainingShare, 0)),
        count: items.length,
    };
}
